import { useState } from "react";
import { Wand2, CopyCheck, RotateCcw, Plus } from "lucide-react";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, NumField } from "@/components/Fields";
import { TYPOLOGIES, convertTypology, DEFAULT_FACE_WIDTH } from "@/lib/typology";
import { applySeries, userSeriesForTypology } from "@/lib/catalogue";
import { useCustomTypologies, applyCustomTypology } from "@/lib/customTypologies";
import { CustomTypologyDialog } from "@/components/CustomTypologyDialog";

export const TypologyBox = ({ item, update }) => {
  const custom = useCustomTypologies();
  const [open, setOpen] = useState(false);
  const pick = (v) => {
    const ct = custom.find((t) => t.key === v);
    if (ct) return update(applyCustomTypology(item, ct));
    const n = convertTypology(item, v), us = userSeriesForTypology(v);
    return update(applySeries(us ? { ...n, profile_system: us.series } : n));
  };
  return (
    <div className="rounded-md border border-cyan-500/30 bg-cyan-500/5 p-3 space-y-3" data-testid="typology-box">
      {item.source_typology && (
        <div>
          <p className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Source joinery typology (from PDF)</p>
          <p className="text-sm text-slate-200 mt-1" data-testid="source-typology">{item.source_typology}</p>
        </div>
      )}
      <Field label="Aluminium typology · convert to">
        <Select value={TYPOLOGIES.some((t) => t.value === item.typology) || custom.some((t) => t.key === item.typology) ? item.typology : "combination"} onValueChange={pick}>
          <SelectTrigger data-testid="typology-select" className="h-9 bg-[#0B0F19] border-slate-700 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#111827] border-slate-700 text-slate-100">
            <SelectGroup>
              <SelectLabel className="text-[10px] font-mono uppercase text-slate-500">Standard</SelectLabel>
              {TYPOLOGIES.map((t) => <SelectItem key={t.value} value={t.value} data-testid={`typology-option-${t.value}`}>{t.label}</SelectItem>)}
            </SelectGroup>
            {custom.length > 0 && (
              <SelectGroup>
                <SelectLabel className="text-[10px] font-mono uppercase text-cyan-400">Custom (your team)</SelectLabel>
                {custom.map((t) => <SelectItem key={t.key} value={t.key} data-testid={`typology-option-${t.key}`}>{t.label}{t.code ? ` · ${t.code}` : ""}</SelectItem>)}
              </SelectGroup>
            )}
          </SelectContent>
        </Select>
      </Field>
      <button data-testid="custom-typology-open-btn" onClick={() => setOpen(true)}
        className="h-8 w-full rounded-md border border-dashed border-cyan-500/50 text-[11px] text-cyan-300 flex items-center justify-center gap-1.5 hover:bg-cyan-500/10 transition-colors">
        <Plus size={12} />Add / manage custom typologies
      </button>
      <p className="flex items-start gap-1.5 text-[11px] text-slate-400"><Wand2 size={12} className="mt-0.5 text-cyan-400 shrink-0" />Changing typology re-maps the panel openings and sets the matching YKK AP series and its face widths. Custom typologies replace the panel layout (scaled to this size). "Combination" keeps the panels exactly as drawn.</p>
      {open && <CustomTypologyDialog item={item} update={update} onClose={() => setOpen(false)} />}
    </div>
  );
};

export const FaceWidths = ({ item, update, applyFaceToAll }) => {
  const set = (k) => (v) => update({ ...item, [k]: v });
  const fw = { frame_width: +item.frame_width || DEFAULT_FACE_WIDTH, mullion_width: +item.mullion_width || DEFAULT_FACE_WIDTH, sash_width: +item.sash_width || DEFAULT_FACE_WIDTH };
  return (
    <div className="space-y-2" data-testid="face-widths">
      <p className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Aluminium face widths (mm) · standard {DEFAULT_FACE_WIDTH}</p>
      <div className="grid grid-cols-3 gap-2">
        <NumField label="Frame" value={item.frame_width} onChange={set("frame_width")} testid="frame-width-input" min={10} />
        <NumField label="Mullion" value={item.mullion_width} onChange={set("mullion_width")} testid="mullion-width-input" min={10} />
        <NumField label="Sash" value={item.sash_width} onChange={set("sash_width")} testid="sash-width-input" min={10} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button data-testid="reset-face-width-btn" onClick={() => update({ ...item, frame_width: DEFAULT_FACE_WIDTH, mullion_width: DEFAULT_FACE_WIDTH, sash_width: DEFAULT_FACE_WIDTH })}
          className="h-8 rounded-md border border-slate-700 text-[11px] text-slate-300 flex items-center justify-center gap-1.5 hover:border-cyan-500 hover:text-cyan-300 transition-colors">
          <RotateCcw size={12} />Reset to {DEFAULT_FACE_WIDTH}
        </button>
        <button data-testid="apply-face-width-all-btn" onClick={() => applyFaceToAll(fw)}
          className="h-8 rounded-md border border-slate-700 text-[11px] text-slate-300 flex items-center justify-center gap-1.5 hover:border-cyan-500 hover:text-cyan-300 transition-colors">
          <CopyCheck size={12} />Apply to all items
        </button>
      </div>
    </div>
  );
};
